import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, MapPin } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { TIPI_PRATICA, STATI_PRATICA, STATO_STYLES, tipoLabel, statoLabel, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LocationSelect from "@/components/LocationSelect";
import { useStudio } from "@/context/StudioContext";

export default function Pratiche() {
  const { settings } = useStudio();
  const practiceTypes = settings?.practice_types?.length ? settings.practice_types : TIPI_PRATICA;
  const [searchParams] = useSearchParams();
  const [practices, setPractices] = useState(null);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("tutti");
  const [tipo, setTipo] = useState("tutti");
  const [vista, setVista] = useState(searchParams.get("vista") === "archivio" ? "archivio" : "attive"); // attive | archivio
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", type: "CILA", client_id: "", provincia: "", comune: "", address: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (practiceTypes.length && !practiceTypes.some((item) => item.value === form.type)) {
      setForm((current) => ({ ...current, type: practiceTypes[0].value }));
    }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    const params = {};
    if (vista === "archivio") params.stato = "conclusa";
    else if (stato !== "tutti") params.stato = stato;
    if (tipo !== "tutti") params.tipo = tipo;
    if (q.trim()) params.q = q.trim();
    API.get("/practices", { params })
      .then((r) => {
        const visiblePractices = vista === "attive" ? r.data.filter((p) => p.status !== "conclusa") : r.data;
        setPractices([...visiblePractices].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)));
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => {
    API.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, stato, tipo, vista]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Seleziona un cliente");
      return;
    }
    setSaving(true);
    try {
      await API.post("/practices", form);
      toast.success("Pratica creata con successo");
      setShowNew(false);
      setForm({ title: "", type: practiceTypes[0]?.value || "CILA", client_id: "", provincia: "", comune: "", address: "", description: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pratiche-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-1">Archivio</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Pratiche Edilizie</h1>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="add-pratica-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuova Pratica
        </Button>
      </div>

      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1.5 w-fit" data-testid="vista-toggle">
        <button
          onClick={() => setVista("attive")}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${vista === "attive" ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="vista-attive-button"
        >
          Pratiche in corso
        </button>
        <button
          onClick={() => setVista("archivio")}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${vista === "archivio" ? "bg-emerald-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="vista-archivio-button"
        >
          Archivio concluse
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per titolo, protocollo o comune..."
            className="pl-9 bg-white"
            data-testid="search-pratiche-input"
          />
        </div>
        {vista === "attive" && (
          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger className="w-full md:w-56 bg-white" data-testid="filter-stato-select">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti gli stati</SelectItem>
              {STATI_PRATICA.filter((s) => s.value !== "conclusa").map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger className="w-full md:w-56 bg-white" data-testid="filter-tipo-select">
            <SelectValue placeholder="Tipologia" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutte le tipologie</SelectItem>
            {practiceTypes.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {practices === null ? (
        <div className="flex justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-800 border-t-transparent" />
        </div>
      ) : practices.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="pratiche-empty">
          <p className="text-slate-500">
            {vista === "archivio"
              ? "Nessuna pratica conclusa in archivio."
              : "Nessuna pratica in corso. Crea la prima pratica con il pulsante \"Nuova Pratica\"."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {practices.map((p) => (
            <Link
              key={p.id}
              to={`/pratiche/${p.id}`}
              className="bg-white rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-5 flex flex-col gap-3"
              data-testid="pratica-card-item"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                  {p.protocol_code}
                </span>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATO_STYLES[p.status] || ""}`}>
                  {statoLabel(p.status)}
                </span>
              </div>
              <div>
                <h3 className="font-heading font-bold text-slate-900 leading-snug">{p.title}</h3>
                <p className="text-xs text-blue-800 font-medium mt-1">{tipoLabel(p.type, practiceTypes)}</p>
              </div>
              <div className="mt-auto space-y-2">
                <p className="text-sm text-slate-600">{p.client?.nome || "—"}</p>
                {p.comune && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {p.comune}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-800 rounded-full" style={{ width: `${p.progress}%` }} />
                  </div>
                  <span className="font-mono text-[10px] text-slate-500">{p.progress}%</span>
                </div>
                <p className="text-[11px] text-slate-400">Aggiornata il {formatDate(p.updated_at)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="new-pratica-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuova Pratica Edilizia</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-2">
              <Label>Titolo pratica *</Label>
              <Input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Es. Ristrutturazione Appartamento Via Roma"
                data-testid="new-pratica-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipologia *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="new-pratica-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {practiceTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="new-pratica-client-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <LocationSelect provincia={form.provincia} comune={form.comune} required onChange={(v) => setForm({ ...form, ...v })} />
            <div className="space-y-2"><Label>Indirizzo / Ubicazione</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="new-pratica-address-input" /></div>
            <div className="space-y-2">
              <Label>Descrizione lavori</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="new-pratica-description-input" />
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-blue-800 hover:bg-blue-900" data-testid="new-pratica-submit-button">
              {saving ? "Creazione..." : "Crea Pratica"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
